import { PreciousMetalWalletBalance } from '@/pages/users/components/UserPreciousMetalWalletBalance';
import { trimAndFilterEmptyParams } from '@/utils/request';
import { RequestData } from '@ant-design/pro-table';
import BigNumber from 'bignumber.js';
import _ from 'lodash';
import dayjs, { Dayjs } from '@/utils/dayjs-config';
import { request } from '@/utils/request';

export interface TransactionsListItem {
  walletId?: string;
  walletLabel?: string;
  walletName?: string;
  currency?: string;
  intent?: string;
  date: string;
  refId: string;
  bookEntryType?: string;
  feeAmount?: string;
  status?: string;
  credit?: string;
  debit?: string;
  balance?: number;
  preservePrecision?: boolean;
  intentId?: string;
  feature?: string;
  transferId?: string;
  bookEntryAccount?: string;
  instructionIntent?: string;
  description?: string;
  userId: string;
  user?: {
    id: string;
    name: string;
    accountNumber: string;
  };
}

export interface periodList {
  period: string;
  symbolAmount: string;
  hkdEquivalent: string;
}
export interface AssetList {
  walletId: string;
  assetSymbol: string;
  totalSymbolAmount: string;
  totalHkdEquivalent: string;
  periodList?: periodList[];
  px: string;
}
export interface RevenueSummaryItem {
  walletId: string;
  walletAccount: string;
  walletLabel: string;
  totalHkdAmount: string;
  periodList?: periodList[];
  assetList: AssetList[];
}

export interface AssetWithAmounts {
  assetSymbol: string;
  salesRevenueUtglAmount: string;
  salesRevenueMgmtAmount: string;
  salesRevenueTeamHeadAmount: string;
  salesRevenueRmAmount: string;
  accountTotalAmount: string;
  salesRevenueUtglHkd: string;
  salesRevenueMgmtHkd: string;
  salesRevenueTeamHeadHkd: string;
  salesRevenueRmHkd: string;
  accountTotalHkd: string;
  walletAccount: string;
  walletLabel: string;
  walletTotalInHkd?: BigNumber;
  px: string;
  periodList?: periodList[];
}

export interface AssetWithWalletTotal extends AssetWithAmounts {
  walletTotalHkd: string;
}
export interface SalesRevenueSummaryItem {
  userAccount: string;
  clientName: string;
  salesRevenueUtglHkd: string;
  salesRevenueMgmtHkd: string;
  salesRevenueTeamHeadHkd: string;
  salesRevenueRmHkd: string;
  accountTotalHkd: string;
  assets: AssetWithAmounts[]; // Add assets array to hold detailed asset information
  priority: number; // Assuming priority is necessary based on the given data
}

export type SalesRevenueStatisticsGrouping = 'month' | 'quarter' | 'year';
export interface SalesRevenueStatisticsParams {
  fromDate?: string;
  toDate?: string;
  grouping?: SalesRevenueStatisticsGrouping;
}
export interface SalesRevenueStatisticsItemRow {
  feeIntent: string;
  assetSymbol: string;
  revenueHkd: string;
  sharePercent: string;
  transactionCount: number;
}
export interface SalesRevenueStatisticsFeeIntentRow extends Omit<SalesRevenueStatisticsItemRow, 'assetSymbol'> {
  assets: SalesRevenueStatisticsItemRow[];
}
export interface SalesRevenueStatisticsResponse {
  period: {
    fromDate: string | null;
    toDate: string | null;
    grouping: SalesRevenueStatisticsGrouping;
    comparisonPeriod?: { fromDate: string; toDate: string; basis: 'calendar' | 'preceding' } | null;
  };
  kpis: {
    totalRevenueHkd: string;
    previousPeriodRevenueHkd: string;
    previousPeriodChangeHkd: string;
    previousPeriodChangePercent: string | null;
    activeSalespersonCount: number;
    revenueGeneratingClientCount: number;
    averageRevenuePerSalespersonHkd: string;
    topSalesperson: { id: string | null; name: string; revenueHkd: string } | null;
  };
  allocationTrend: Array<{ period: string; utglHkd: string; managementHkd: string; teamHeadHkd: string; rmHkd: string; totalHkd: string }>;
  revenueByIntentTrend?: Array<{ period: string; totalHkd: string; intents: Array<{ feeIntent: string; revenueHkd: string }> }>;
  salespersons: Array<{
    rank: number;
    id: string | null;
    name: string;
    accountReference: string | null;
    clientCount: number;
    attributedRevenueHkd: string;
    revenueSharePercent: string;
    averageRevenuePerClientHkd: string;
    previousPeriodRevenueHkd: string;
    deltaHkd: string;
    deltaPercent: string | null;
  }>;
  salesTeamAccounts?: Array<{
    rank: number;
    id: string | null;
    name: string;
    accountReference: string | null;
    relationshipManagerName: string | null;
    clientCount: number;
    attributedRevenueHkd: string;
    revenueSharePercent: string;
    averageRevenuePerClientHkd: string;
    previousPeriodRevenueHkd: string;
    deltaHkd: string;
    deltaPercent: string | null;
  }>;
  topRevenueItems: SalesRevenueStatisticsItemRow[];
  /** Optional during rolling deployments; legacy top items must not be treated as complete totals. */
  revenueByFeeIntent?: SalesRevenueStatisticsFeeIntentRow[];
  topRevenueClients?: Array<{
    rank: number;
    id: string;
    name: string;
    portalId: string | null;
    relationshipManagerName: string | null;
    revenueHkd: string;
    feeIntents: Array<{ feeIntent: string; revenueHkd: string; sharePercent: string; transactionCount: number }>;
  }>;
  dataQuality: { status: 'complete' | 'partial' | 'empty'; missingFxAssets: string[]; costBasis: 'unavailable'; notes: string[] };
  profitability: { available: false; reason: string };
}
export interface BankAccountListItem {
  id?: string;
  userId?: string;
  status?: string;
  accountHolderName?: string;
  accountNumber?: string;
  accountType?: string;
  bankDetail?: string;
  bankName?: string;
  country?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WalletBalanceItem {
  availableBalanceUsdEquivalent: string;
  totalBalanceUsdEquivalent: string;
  availableBalanceHkdEquivalent: string;
  totalBalanceHkdEquivalent: string;
  currencies: Currency[];
  wallets: Wallet[];
}
export interface Currency {
  totalBalance: string;
  availableBalance: string;
  totalBalanceUsdEquivalent: string;
  availableBalanceUsdEquivalent: string;
  asset: string;
}

export interface Balance {
  availableBalance: string;
  totalBalance: string;
  availableBalanceHkdEquivalent: string;
  availableBalanceUsdEquivalent: string;
  totalBalanceHkdEquivalent: string;
  totalBalanceUsdEquivalent: string;
  asset: string;
  assetType: string;
  address: string;
  ledgerBalance: string;
}

export interface FundBalance {
  fundName: string;
  fundCurrency: string;
  units: string;
  availableUnits?: string;
  marketValueInFundCurrency: string;
  usdEquivalent: string;
  hkdEquivalent: string;
  unrealizedGainLoss?: string;
  percentChange?: string;
}

export interface Wallet {
  balances: Balance[];
  id: string;
  label: string;
  name: string;
  accountNumber: string;
  totalBalanceUsdEquivalent: string;
  availableBalanceUsdEquivalent: string;
  totalBalanceHkdEquivalent: string;
  availableBalanceHkdEquivalent: string;
  overdraft?: boolean;
  preciousMetals?: PreciousMetalWalletBalance[];
  funds?: FundBalance[];
}

export interface WalletBalancesData {
  data: Wallet[];
  total: number;
}

export interface WalletsRes {
  run?: () => void;
  data: WalletBalancesData;
  loading?: boolean;
  refresh?: () => void;
}

export interface WalletSolution {
  balances?: Balance[];
  id?: string;
  label?: string;
  name?: string;
  accountNumber?: string;
  totalBalanceUsdEquivalent?: string;
  availableBalanceUsdEquivalent?: string;
  totalBalanceHkdEquivalent?: string;
  availableBalanceHkdEquivalent?: string;
  overdraft?: boolean;
  asset?: string;
}

export interface AllInstructionListItem {
  time: Date;
  accountNumber: string;
  email: string;
  intent: InstructionIntent;
  qty1: string;
  ccy1: string;
  qty2: string;
  ccy2: string;
  rate: string;
  status: string;
  refId: string;
  intentId: string;
  userId: string;
  userName: string;
  /** Feature source table (e.g., 'bill_payment' for legacy, 'general_instruction' for new) */
  feature?: string;
}
export interface GetWalletBalancesParams {
  userId: string;
  current: number;
  pageSize: number;
  keyword?: string;
  asset?: string;
  accountNumbers?: string[];
  hideLowBalance?: boolean;
  walletId?: string;
  /** Selector mode - skip additional accounts (securities, insurance, earn, etc.) for faster dropdown loading */
  isSelector?: boolean;
}

export interface Currency {
  totalBalance: string;
  availableBalance: string;
  totalBalanceUsdEquivalent: string;
  availableBalanceUsdEquivalent: string;
  totalBalanceHkdEquivalent: string;
  availableBalanceHkdEquivalent: string;
  ledgerBalance: string;
  asset: string;
}

export interface WalletSummary {
  availableBalanceUsdEquivalent: string;
  totalBalanceUsdEquivalent: string;
  availableBalanceHkdEquivalent: string;
  totalBalanceHkdEquivalent: string;
}

export interface RevenueBreakDownItem {
  intent: string;
  instructionCount: string;
  period: string;
  subTotal: string;
  px: string;
  assetSymbol: string;
  originalSubTotal: string;
}

export enum InstructionStatus {
  NEW_AND_PENDING = 'NEW_AND_PENDING',
  CANCEL = 'CANCEL',
  CAPTURED = 'CAPTURED',
  COMPLETED = 'COMPLETED',
  DECLINED = 'DECLINED',
  LOCKED = 'LOCKED',
  SCHEDULED = 'SCHEDULED',
  SUCCESS = 'SUCCESS',
  USER_CANCEL = 'USER_CANCEL',
  VOID = 'VOID',
  REJECTED = 'REJECTED'
}

export enum InstructionFeature {
  DIGITAL_DEPOSIT = 'DIGITAL_DEPOSIT',
  WITHDRAW_FIAT = 'WITHDRAW_FIAT',
  WITHDRAW_DIGITAL = 'WITHDRAW_DIGITAL',
  DEPOSIT_FIAT = 'DEPOSIT_FIAT',
  COMMODITIES = 'COMMODITIES',
  COMMODITIES_DEPOSIT = 'COMMODITIES_DEPOSIT',
  COMMODITIES_WITHDRAW = 'COMMODITIES_WITHDRAW',
  TRADER_BURN = 'TRADER_BURN',
  TRADER_MINT = 'TRADER_MINT',
  REPAY = 'REPAY',
  EXCHANGE = 'EXCHANGE',
  BILL_PAYMENT = 'BILL_PAYMENT',
  TRANSFER_P2P = 'TRANSFER_P2P',
  TRANSFER_SELF = 'TRANSFER_SELF',
  GENERAL_INSTRUCTION = 'GENERAL_INSTRUCTION',
  FEE_PAYOUT = 'FEE_PAYOUT'
}

export enum InstructionIntent {
  ADJUSTMENT = 'ADJUSTMENT',
  ASSET_INJECTION = 'ASSET_INJECTION',
  ASSET_WITHDRAWAL = 'ASSET_WITHDRAWAL',
  BILL_PAYMENT = 'BILL_PAYMENT',
  CARD_PAYMENT = 'CARD_PAYMENT',
  CHARGE = 'CHARGE',
  COMMISSION = 'COMMISSION',
  COMMODITIES = 'COMMODITIES',
  COMMODITIES_DEPOSIT = 'COMMODITIES_DEPOSIT',
  COMMODITIES_WITHDRAW = 'COMMODITIES_WITHDRAW',
  DEPOSIT_DIGITAL = 'DEPOSIT_DIGITAL',
  DEPOSIT_FIAT = 'DEPOSIT_FIAT',
  EXCHANGE = 'EXCHANGE',
  FOREX = 'FOREX',
  INSURANCE = 'INSURANCE',
  REPAY = 'REPAY',
  SECURITIES_APPLICATION = 'SECURITIES_APPLICATION',
  SECURITIES_TRANSFER = 'SECURITIES_TRANSFER',
  TRADER_BURN = 'TRADER_BURN',
  TRADER_MINT = 'TRADER_MINT',
  TRANSFER = 'TRANSFER',
  TRANSFER_P2P = 'TRANSFER_P2P',
  TRANSFER_SELF = 'TRANSFER_SELF',
  WITHDRAW_DIGITAL = 'WITHDRAW_DIGITAL',
  WITHDRAW_FIAT = 'WITHDRAW_FIAT',
  FEE_PAYOUT = 'FEE_PAYOUT',
  FUND_SUBSCRIPTION = 'FUND_SUBSCRIPTION',
  BOND_SUBSCRIPTION = 'BOND_SUBSCRIPTION',
  CHANGE_BENEFICIARIES = 'CHANGE_BENEFICIARIES',
  INSURANCE_ENQUIRY = 'INSURANCE_ENQUIRY',
  SETTLEMENT = 'SETTLEMENT',
  EXCHANGE_SUB_ACCOUNT_APPLICATION = 'EXCHANGE_SUB_ACCOUNT_APPLICATION',
  EXCHANGE_SUB_ACCOUNT_TRANSFER = 'EXCHANGE_SUB_ACCOUNT_TRANSFER',
  EXCHANGE_SUB_ACCOUNT_MONTHLY_FEE = 'EXCHANGE_SUB_ACCOUNT_MONTHLY_FEE',
  FUND_DISTRIBUTION = 'FUND_DISTRIBUTION',
  FEE = 'FEE',
  PAYMENT = 'PAYMENT',
  OTHER = 'OTHER',
  DIGITAL_DEPOSIT = 'DIGITAL_DEPOSIT',
  DIGITAL_WITHDRAWAL = 'DIGITAL_WITHDRAWAL',
  CARD_APPLICATION = 'CARD_APPLICATION',
  REMITTANCE = 'REMITTANCE',
  GENERAL_INSTRUCTION = 'GENERAL_INSTRUCTION',
  EARN_SUBSCRIPTION = 'EARN_SUBSCRIPTION',
  EARN_REDEMPTION = 'EARN_REDEMPTION'
}

export enum InstructionEntryType {
  'CREDIT' = 'CREDIT',
  'DEBIT' = 'DEBIT',
  'NONE' = 'NONE'
}

export enum InstructionStatusGroup {
  NEW = 'New',
  REQUESTED = 'Requested',
  PROCESSING = 'Processing',
  PENDING = 'Pending',
  PENDING_FUNDING = 'Pending Funding',
  PENDING_SETTLEMENT = 'Pending Settlement',
  PENDING_PAYMENT = 'Pending Payment',
  PENDING_APPROVAL = 'Pending Approval',
}

export const noneTradableWalletLabels: string[] = ['insurance', 'securities', 'alternative'];

export async function getTransactionsV2(params: any): Promise<RequestData<TransactionsListItem>> {
  if (params.date) {
    params.fromDate = params.date[0];
    params.toDate = params.date[1];
    delete params.date;
  }
  //when using get method, that intent may string (when length == 1) | string[], to consistent, join it with comma as string and seperate in backend
  if (Array.isArray(params.intent) && params.intent.length > 0) {
    params.intent = params.intent.join(',');
  }
  const data = await request<RequestData<TransactionsListItem>>('/wallet/transactions-v2', {
    params
  });
  return data;
}

export async function getTransactionsOrRevenue(params: any): Promise<RequestData<TransactionsListItem>> {
  if (params.date) {
    params.fromDate = params.date[0];
    params.toDate = params.date[1];
    delete params.date;
  }

  const data = await request<RequestData<TransactionsListItem>>('/wallet/revenue-transactions', {
    params
  });
  return data;
}

export async function getRevenueSummary(params: any): Promise<RequestData<RevenueSummaryItem>> {
  if (params.date) {
    params.fromDate = params.date[0];
    params.toDate = params.date[1];
    delete params.date;
  }

  const data = await request<RequestData<RevenueSummaryItem>>('/wallet/revenue-summary', {
    params
  });
  return data;
}

export async function getSalesRevenueSummary(params: any): Promise<RequestData<SalesRevenueSummaryItem>> {
  if (params.date) {
    params.fromDate = params.date[0];
    params.toDate = params.date[1];
    delete params.date;
  }

  const data = await request<RequestData<SalesRevenueSummaryItem>>('/wallet/sales-revenue-summary', {
    params
  });
  return data;
}

export async function getSalesRevenueStatistics(params: SalesRevenueStatisticsParams): Promise<SalesRevenueStatisticsResponse> {
  const query = new URLSearchParams();
  if (params.fromDate) query.set('fromDate', params.fromDate);
  if (params.toDate) query.set('toDate', params.toDate);
  if (params.grouping) query.set('grouping', params.grouping);
  const suffix = query.toString();
  return request<SalesRevenueStatisticsResponse>(`/wallet/sales-revenue-statistics${suffix ? `?${suffix}` : ''}`);
}

export async function getRevenueBreakDown(params: any): Promise<RevenueBreakDownItem[]> {
  if (params.date) {
    params.fromDate = params.date[0];
    params.toDate = params.date[1];
    delete params.date;
  }

  const data = await request<RevenueBreakDownItem[]>('/wallet/revenue-breakdown', {
    params
  });
  return data;
}

export async function getAllInstruction(params: {
  userId?: string;
  intent?: string;
  fromDate?: string;
  toDate?: string;
  vendorId?: number;
  refId?: string;
  status?: string;
  date?: Dayjs[];
  searchStatus?: string;
}): Promise<RequestData<AllInstructionListItem>> {
  if (params.date && params.date.length > 0) {
    params.fromDate = dayjs(params.date[0]).format('YYYY-MM-DD');
    params.toDate = dayjs(params.date[1]).format('YYYY-MM-DD');
    delete params.date;
  }
  const data = await request<RequestData<AllInstructionListItem>>('/wallet/instruction/all', {
    params
  });
  return data;
}

export async function getBankAccount(params: any): Promise<RequestData<BankAccountListItem>> {
  // if (params.date) {
  //   params.fromDate = params.date[0];
  //   params.toDate = params.date[1];
  //   delete params.date;
  // }
  const data = await request<RequestData<BankAccountListItem>>('/user-bank-accounts', {
    params
  });
  return data;
}

export async function getWalletBalances(params: GetWalletBalancesParams) {
  const accountNumbers = params.accountNumbers;
  const tempParams = _.omit(_.cloneDeep(params), 'accountNumbers');
  _.forEach(accountNumbers, (value, index) => {
    tempParams[`accountNumbers[${index}]`] = value;
  });
  return request<WalletBalancesData>('/wallet/balances', {
    params: trimAndFilterEmptyParams(tempParams),
    getResponse: true
  });
}

export interface WalletsAvailableBalances {
  id: string;
  userId: string;
  name?: string;
  label: string;
  asset: string;
  availableBalance: string;
  totalBalance: string;
  availableBalanceHkdEquivalent: string;
  totalBalanceHkdEquivalent: string;
  overdraft?: boolean;
}

interface getWalletsAvailableBalancesParams {
  userId: string;
  walletId?: string;
  keyword?: string;
  asset?: string;
  raw?: boolean;
}

export async function getWalletsAvailableBalances(params: getWalletsAvailableBalancesParams) {
  return request<WalletsAvailableBalances[]>('/wallet/available-balances', {
    params: trimAndFilterEmptyParams(params),
    getResponse: true
  });
}

export async function queryWalletsAvailableBalances(params: getWalletsAvailableBalancesParams) {
  return request<WalletsAvailableBalances[]>('/wallet/available-balances', {
    params: trimAndFilterEmptyParams(params)
  });
}

export async function getWalletSummary(params: { userId: string }) {
  return request<WalletSummary>('/wallet/summary', {
    params,
    getResponse: true
  });
}

export const getEarnMaturityDateText = (date: Date | string | null) => {
  if (date === null) {
    return 'Flexible';
  }

  const today = dayjs();
  const maturityDate = dayjs(date);
  const daysLeft = today.diff(maturityDate, 'days');
  const daysDiff = Math.abs(daysLeft);

  if (daysDiff === 0) {
    return 'Today';
  }

  // Check if the maturity date is expired
  if (today.isAfter(maturityDate)) {
    if (daysDiff === 1) {
      return `${daysDiff} Day Before`;
    }
    return `${daysDiff} Days Before`;
  }

  if (daysDiff === 1) {
    return `In ${daysDiff} day`;
  }

  return `In ${daysDiff} Days`;
};

export const getPreciousMetalWalletBalances = (params: { userId: string }) => {
  return request<WalletsAvailableBalances[]>('/wallet/precious-metal-wallet-balances', {
    params,
    getResponse: true
  });
};
